<template>
    <div>
        <div v-if="!isShowBankingInformation && !isShowInvoicingInformation">
            <div class="mt-5">
                <div class="mb-3 text-right">
                    <hts-form-button
                        v-if="group.isAdmin || permissions.includes('company_branch_add')"
                        icon-name="hi-plus-circle-icon"
                        icon-position="right"
                        type="submit"
                        @click="showSlideOverAddBranch = true">
                        {{ $t('labels.addCompanyBranch') }}
                    </hts-form-button>
                </div>
            </div>
            <div class="body">
                <hts-data-table
                    ref="dataTables"
                    :headers="companyBranchHeaders"
                    :server="getCompanyBranch"
                    :get-context-menu-items="getContextMenuItems" />
            </div>
            <add-branch
                :show="showSlideOverAddBranch"
                @close="showSlideOverAddBranch = false"
                @refresh-tables="refreshTables()"
                @action-add-branch="(newBranch) => actionAddBranch(newBranch)" />
            <edit-branch
                :branch-is-selected-for-edit="branchIsSelectedToEdit"
                :show="showSlideOverEditBranch"
                @refresh-tables="refreshTables()"
                @close="showSlideOverEditBranch = false" />
            <hts-modal
                :show="showPopupDeleteBranch"
                :title="$t('labels.deleteBranch')"
                type="confirm"
                :content="$t('labels.confirmDeleteBranch')"
                @hide="closePopupDeleteBranch"
                @confirm="handleDeleteBranch" />
        </div>
        <invoicing-branch
            v-if="isShowInvoicingInformation"
            :invoice-is-selected-to-view="invoiceIsSelectedToView"
            :company-branch="branchIsSelectedToView"
            @back="isShowInvoicingInformation = false" />
        <banking-branch
            v-if="isShowBankingInformation"
            :banking-is-selected-to-view="bankingIsSelectedToView"
            :company-branch="branchIsSelectedToView"
            @back="isShowBankingInformation = false" />
    </div>
</template>

<script setup lang="ts">
import AddBranch from './ignore/add-branch.vue'
import BankingBranch from './ignore/banking-branch.vue'
import EditBranch from './ignore/edit-branch.vue'
import { IBranch } from './ignore/imports/form'
import { IInvoicingInformation } from './ignore/imports/invoice-form'
import InvoicingBranch from './ignore/invoicing-branch.vue'
import companyPageTabs from '@/data/company/tabs.json'

const route = useRoute()
const store = $store()
const group = store.system.getGroup
const permissions = ref<Array<String>>([])
onMounted(() => {
  permissions.value = group.permissions
})
onBeforeMount(() => {
  route.meta.pageTabs = companyPageTabs
})

const companyBranchHeaders = [
  {
    field: 'name',
    label: 'name',
    headerName: $t('labels.branchName'),
    sortable: true
  },
  {
    field: 'taxNumber',
    label: 'taxNumber',
    headerName: $t('labels.taxNumber'),
    sortable: true
  },
  {
    field: 'website',
    label: 'website',
    headerName: $t('labels.website'),
    sortable: true
  },
  {
    field: 'address',
    label: 'address',
    headerName: $t('labels.address'),
    sortable: true
  }
]
const getContextMenuItems = (params: any) => {
  if (!params.node?.data) {
    return null
  }
  const menuItem = [
    group.isAdmin || permissions.value.includes('company_branch_edit')
      ? {
        name: $t('labels.edit'),
        action: () => {
          handleEditBranch(params.node?.data || {})
        }
      }
      : null,
    group.isAdmin || permissions.value.includes('company_branch_delete')
      ? {
        name: $t('labels.delete'),
        action: () => {
          showPopupDeleteBranch.value = true
          companyBranchId.value = params.node?.data._id
        }
      }
      : null,
    {
      name: $t('labels.viewInvoicingInformation'),
      action: () => {
        isShowInvoicingInformation.value = true
        Object.assign(branchIsSelectedToView, params.node?.data || {})
        Object.assign(invoiceIsSelectedToView, params.node?.data.invoicingInformation)
      }
    },
    {
      name: $t('labels.viewBankingInformation'),
      action: () => {
        isShowBankingInformation.value = true
        Object.assign(branchIsSelectedToView, params.node?.data || {})
        bankingIsSelectedToView.value = params.node?.data.bankingInformation
      }
    }
  ]
  return menuItem
}
const getCompanyBranch = $url.company.getCompanyBranch
const dataTables = ref()
const isShowBankingInformation = ref(false)
const isShowInvoicingInformation = ref(false)
const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}
const listBranch = ref<IBranch[]>([])
const bankingIsSelectedToView = ref([])
const showSlideOverAddBranch = ref(false)
const showSlideOverViewInvoice = ref(false)
const showPopupDeleteBranch = ref(false)
const showSlideOverEditBranch = ref(false)
const companyBranchId = ref('')
const invoiceIsSelectedToView = reactive<IInvoicingInformation>({
  name: '',
  address: '',
  accountNumber: '',
  accountName: '',
  swiftCode: '',
  taxNumber: '',
  phone: ''
})
const branchIsSelectedToView = reactive<IBranch>({
  workspace: '',
  type: '',
  name: '',
  taxNumber: '',
  website: '',
  address: '',
  logoUrl: '',
  status: 0,
  workday: 0,
  currency: ''
})
const branchIsSelectedToDelete = reactive<IBranch>({
  workspace: '',
  type: '',
  name: '',
  taxNumber: '',
  website: '',
  address: '',
  logoUrl: '',
  status: 0,
  workday: 0,
  currency: ''
})
const branchIsSelectedToEdit = reactive<IBranch>({
  workspace: '',
  type: '',
  name: '',
  taxNumber: '',
  website: '',
  address: '',
  logoUrl: '',
  status: 0,
  workday: 0,
  currency: ''
})
const actionAddBranch = (newBranch: any) => {
  listBranch.value = [newBranch, ...listBranch.value]
}
const handleDeleteBranch = (branch: any) => {
  const api = {
    url: '/company/branch/delete-branch/' + companyBranchId.value,
    method: 'delete'
  }
  $api(api).then((data: any) => {
    const resData: any = data.data.value
    const resError: any = data.error
    if (resData) {
      if (resData.success) {
        listBranch.value = $lodash.filter(listBranch.value, (value: any) => value.id !== branch.branch.id)
        process.client && $toast().success($t('labels.deleteBranchSuccess'))
      }
    } else if (resError.value.data === undefined) {
      process.client && $toast().error($t('labels.canNotConnectServer'))
    } else {
      process.client && $toast().error(resError.value.data.errors[0].msg)
    }
    refreshTables()
    showPopupDeleteBranch.value = false
  })
}
const closePopupDeleteBranch = () => {
  showPopupDeleteBranch.value = false
  Object.assign(branchIsSelectedToDelete, {})
}
const handleEditBranch = (branch: Object) => {
  Object.assign(branchIsSelectedToEdit, { ...branch })
  showSlideOverEditBranch.value = true
}
</script>
